import React, { useMemo, useState } from 'react';
import { ShiftRecord } from '../types';
import { usePos } from '../context/PosContext';
import * as db from '../services/db';
import { formatDate, formatDateTime } from '../services/datetime';
import { tenderTotals } from '../services/payments';
import { buildShiftEndEscPos, buildDayCloseEscPos } from '../services/escpos';
import { usbPrinterConfigured, printBytes } from '../services/usbPrinter';
import './Sections.css';

const sameDay = (iso: string, d: Date) => {
  const x = new Date(iso);
  return x.getFullYear() === d.getFullYear() && x.getMonth() === d.getMonth() && x.getDate() === d.getDate();
};

// Shift End — drives the shift session machine (Android Shift bean: one open
// shift at a time, numbered within the day). Bills recorded while a shift is
// open carry its id; closing snapshots a ShiftRecord and either starts the
// next shift or ends the day.
const ShiftEnd: React.FC = () => {
  const { sales, cashflow, formatMoney, config, activeShift, openShift, closeShift } = usePos();
  const [openingCash, setOpeningCash] = useState(() =>
    String(activeShift?.openingCash ?? db.getOpeningBalance(new Date().toISOString().split('T')[0]))
  );

  const today = new Date();

  const summary = useMemo(() => {
    // Bills belonging to this shift: stamped with its id, or (legacy bills
    // recorded before sessions existed) today's bills since it opened.
    const openedAt = activeShift ? new Date(activeShift.openedAt) : new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const shiftSales = sales.filter((s) => {
      if (s.status !== 'completed') return false;
      if (activeShift && s.shiftId) return s.shiftId === activeShift.id;
      return sameDay(s.timestamp, today) && new Date(s.timestamp) >= openedAt;
    });
    const t = tenderTotals(shiftSales);
    const totalSales = shiftSales.reduce((a, s) => a + s.total, 0);
    const cashSales = t.cash;
    const cardSales = t.card;
    const digitalSales = t.digital;
    const shiftCash = cashflow.filter((c) => sameDay(c.timestamp, today) && new Date(c.timestamp) >= openedAt);
    const expenses = shiftCash.filter((c) => c.type === 'expense').reduce((a, c) => a + c.amount, 0);
    const received = shiftCash
      .filter((c) => c.type !== 'expense')
      .reduce((a, c) => a + (c.paymentMethod === 'cash' ? c.amount : 0), 0);
    const opening = parseFloat(openingCash) || 0;
    const expectedCash = opening + cashSales + received - expenses;
    // GST / estimate / non-GST split (Android EstimateShift / NonGstShift).
    const estimateSales = shiftSales.filter((s) => s.estimate).reduce((a, s) => a + s.total, 0);
    const nonGstSales = shiftSales.filter((s) => !s.estimate && s.gstFlag === false).reduce((a, s) => a + s.total, 0);
    const gstSales = totalSales - estimateSales - nonGstSales;
    const taxCollected = shiftSales.reduce((a, s) => a + s.tax + (s.cess ?? 0), 0);
    return { shiftSales, totalSales, cashSales, cardSales, digitalSales, expenses, expectedCash, opening, gstSales, estimateSales, nonGstSales, taxCollected };
    // eslint-disable-next-line
  }, [sales, cashflow, openingCash, activeShift]);

  const buildRecord = (): ShiftRecord => ({
    id: db.newId(),
    shiftNo: activeShift?.shiftNo,
    dayId: activeShift?.dayId ?? today.toISOString().split('T')[0],
    openedAt: activeShift?.openedAt ?? new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString(),
    closedAt: new Date().toISOString(),
    openingCash: summary.opening,
    totalSales: summary.totalSales,
    cashSales: summary.cashSales,
    cardSales: summary.cardSales,
    digitalSales: summary.digitalSales,
    expenses: summary.expenses,
    expectedCash: summary.expectedCash,
    billCount: summary.shiftSales.length,
  });

  // Close the running shift; optionally open the next one with the drawer
  // cash carried forward as its opening amount. Closing a shift to start the
  // next prints that shift's slip; ending the day prints the consolidated
  // Day Close report (all shifts of the day combined).
  const endShift = async (startNext: boolean) => {
    const verb = startNext ? 'Close this shift and start the next one?' : 'End the day and close this shift?';
    if (!window.confirm(verb)) return;
    await closeShift(buildRecord(), startNext, summary.expectedCash);
    if (startNext) {
      printSummary();
      setOpeningCash(String(summary.expectedCash));
    } else {
      // The current shift is now saved (closeShift → db.saveShift), so the
      // day total below includes it.
      await printDayClose();
    }
    alert(startNext ? 'Shift closed — next shift opened.' : 'Day ended — shift closed.');
  };

  const startShift = async () => {
    const shift = await openShift(parseFloat(openingCash) || 0);
    alert(`Shift ${shift.shiftNo} opened.`);
  };

  // Shift-end slip: prints to the USB thermal printer when one is configured,
  // otherwise falls back to a browser print window.
  const printSummary = () => {
    const label = activeShift ? `SHIFT ${activeShift.shiftNo}` : 'SHIFT';
    const rows: [string, string][] = [
      ['Bills', String(summary.shiftSales.length)],
      ['Opening Cash', formatMoney(summary.opening)],
      ['-', ''],
      ['Cash Sales', formatMoney(summary.cashSales)],
      ['Card Sales', formatMoney(summary.cardSales)],
      ['Digital Sales', formatMoney(summary.digitalSales)],
      ['Total Sales', formatMoney(summary.totalSales)],
      ['-', ''],
      ['GST Sales', formatMoney(summary.gstSales)],
      ['Estimate Sales', formatMoney(summary.estimateSales)],
      ['Non-GST Sales', formatMoney(summary.nonGstSales)],
      ['Tax Collected', formatMoney(summary.taxCollected)],
      ['Expenses', formatMoney(summary.expenses)],
      ['-', ''],
      ['Expected Cash in Drawer', formatMoney(summary.expectedCash)],
    ];

    const printWindow = () => {
      const c = [
        `${config.storeName} — ${label} END`,
        formatDate(today),
        '==============================',
        ...rows.map(([l, r]) => (l === '-' ? '------------------------------' : `${l}: ${r}`)),
        '==============================',
      ].join('\n');
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(`<pre>${c}</pre>`);
        w.document.close();
        w.print();
      }
    };

    if (usbPrinterConfigured()) {
      printBytes(buildShiftEndEscPos(config.storeName, label, formatDate(today), rows)).catch((e) => {
        console.warn('USB shift-end print failed, falling back to browser print:', e);
        printWindow();
      });
      return;
    }
    printWindow();
  };

  // Day Close report — the whole day's totals across ALL shifts (not just the
  // current session). Aggregates every completed bill and cash entry dated
  // today; opening cash is the day's first-shift opening (per-day balance).
  const daySummary = async () => {
    const dayId = today.toISOString().split('T')[0];
    const daySales = sales.filter((s) => s.status === 'completed' && sameDay(s.timestamp, today));
    const t = tenderTotals(daySales);
    const totalSales = daySales.reduce((a, s) => a + s.total, 0);
    const dayCash = cashflow.filter((c) => sameDay(c.timestamp, today));
    const expenses = dayCash.filter((c) => c.type === 'expense').reduce((a, c) => a + c.amount, 0);
    const received = dayCash
      .filter((c) => c.type !== 'expense')
      .reduce((a, c) => a + (c.paymentMethod === 'cash' ? c.amount : 0), 0);
    const opening = await db.getOpeningBalance(dayId);
    const expectedCash = opening + t.cash + received - expenses;
    const estimateSales = daySales.filter((s) => s.estimate).reduce((a, s) => a + s.total, 0);
    const nonGstSales = daySales.filter((s) => !s.estimate && s.gstFlag === false).reduce((a, s) => a + s.total, 0);
    const gstSales = totalSales - estimateSales - nonGstSales;
    const taxCollected = daySales.reduce((a, s) => a + s.tax + (s.cess ?? 0), 0);
    const shiftCount = (await db.getShifts()).filter((s) => s.dayId === dayId).length;
    return {
      billCount: daySales.length,
      shiftCount,
      opening,
      cashSales: t.cash,
      cardSales: t.card,
      digitalSales: t.digital,
      totalSales,
      gstSales,
      estimateSales,
      nonGstSales,
      taxCollected,
      expenses,
      expectedCash,
    };
  };

  // Day-close slip: prints the consolidated day total to the USB thermal
  // printer when one is configured, otherwise to a browser print window.
  const printDayClose = async () => {
    const d = await daySummary();
    const rows: [string, string][] = [
      ['Shifts', String(d.shiftCount)],
      ['Bills', String(d.billCount)],
      ['Day Opening Cash', formatMoney(d.opening)],
      ['-', ''],
      ['Cash Sales', formatMoney(d.cashSales)],
      ['Card Sales', formatMoney(d.cardSales)],
      ['Digital Sales', formatMoney(d.digitalSales)],
      ['Total Sales', formatMoney(d.totalSales)],
      ['-', ''],
      ['GST Sales', formatMoney(d.gstSales)],
      ['Estimate Sales', formatMoney(d.estimateSales)],
      ['Non-GST Sales', formatMoney(d.nonGstSales)],
      ['Tax Collected', formatMoney(d.taxCollected)],
      ['Expenses', formatMoney(d.expenses)],
      ['-', ''],
      ['Expected Cash in Drawer', formatMoney(d.expectedCash)],
    ];

    const printWindow = () => {
      const c = [
        `${config.storeName} — DAY CLOSE`,
        formatDate(today),
        '==============================',
        ...rows.map(([l, r]) => (l === '-' ? '------------------------------' : `${l}: ${r}`)),
        '==============================',
      ].join('\n');
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(`<pre>${c}</pre>`);
        w.document.close();
        w.print();
      }
    };

    if (usbPrinterConfigured()) {
      printBytes(buildDayCloseEscPos(config.storeName, formatDate(today), rows)).catch((e) => {
        console.warn('USB day-close print failed, falling back to browser print:', e);
        printWindow();
      });
      return;
    }
    printWindow();
  };

  const Row = ({ label, value, strong }: { label: string; value: number; strong?: boolean }) => (
    <div className="b2-totrow" style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ fontWeight: strong ? 700 : 400, color: strong ? 'var(--color-primary-darker)' : undefined }}>{label}</span>
      <span style={{ fontWeight: strong ? 700 : 400 }}>{formatMoney(value)}</span>
    </div>
  );

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>Shift End</h1>
        <span className="muted">{formatDate(today)}</span>
      </div>

      <div className="section-card">
        {activeShift ? (
          <h2>
            Shift {activeShift.shiftNo} — open since {formatDateTime(activeShift.openedAt)}
          </h2>
        ) : (
          <h2>No shift open</h2>
        )}
        {!activeShift && (
          <p className="muted">
            Open a shift to start a numbered session — bills are stamped with the shift they
            belong to. (Recording a sale auto-opens today's first shift.)
          </p>
        )}
        <div className="field-row">
          <div className="field" style={{ maxWidth: 200 }}>
            <label>Opening Cash</label>
            <input
              type="number"
              value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value)}
              disabled={!!activeShift}
            />
          </div>
          <div className="field">
            <label>Bills</label>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-primary-dark)' }}>{summary.shiftSales.length}</div>
          </div>
        </div>

        <div style={{ maxWidth: 420 }}>
          <Row label="Cash Sales" value={summary.cashSales} />
          <Row label="Card Sales" value={summary.cardSales} />
          <Row label="Digital Sales" value={summary.digitalSales} />
          <Row label="Total Sales" value={summary.totalSales} strong />
          <div className="b2-divider" style={{ height: 1, background: 'var(--color-border)', margin: '6px 0' }} />
          <Row label="GST Sales" value={summary.gstSales} />
          {summary.estimateSales > 0 && <Row label="Estimate Sales" value={summary.estimateSales} />}
          {summary.nonGstSales > 0 && <Row label="Non-GST Sales" value={summary.nonGstSales} />}
          <Row label="Tax Collected (GST+CESS)" value={summary.taxCollected} />
          <div className="b2-divider" style={{ height: 1, background: 'var(--color-border)', margin: '6px 0' }} />
          <Row label="Expenses" value={summary.expenses} />
          <div className="b2-divider" style={{ height: 2, background: 'var(--color-primary)', margin: '8px 0' }} />
          <Row label="Expected Cash in Drawer" value={summary.expectedCash} strong />
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn-grey" onClick={printSummary}>
            Print Summary
          </button>
          {activeShift ? (
            <>
              <button className="btn-teal" onClick={() => endShift(true)}>
                Close Shift &amp; Start Next
              </button>
              <button className="btn-teal" style={{ background: 'var(--color-accent, #f44336)' }} onClick={() => endShift(false)}>
                Day End
              </button>
            </>
          ) : (
            <button className="btn-teal" onClick={startShift}>
              Open Shift
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShiftEnd;
